import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowRightOutlined, ListOutlined, PublishOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Publish } from '@/components/events/detail/tabs/flows/grid/actions/publish';
import { useFlowPlanModeHeaderState } from '../use-flow-plan-mode-header-state';
import SlotList from '../slot-list';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDetailMode } from '../flow-detail.types';
import type { FlowSlotAssetsWideAnchor } from './flow-slot-assets-wide-anchor';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { slotListColumnHeaderInsetSx, slotListColumnOuterSx, slotListScrollAreaSx } from './flow-slot-list-column-sx';
import type { Mode } from '@/types/entity-mode';

export type FlowSlotAssetsSlotListColumnProps = {
  setShowFlowDetails: Dispatch<SetStateAction<FlowDetailMode | undefined>>;
  setFlowDetailsDirection: Dispatch<SetStateAction<'up' | 'down' | undefined>>;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  middleSectionOpensOverlayOnSmallScreen?: boolean;
  flowSlotAssetsWideAnchor?: FlowSlotAssetsWideAnchor;
  onFlowSlotAssetsWideAnchorChange?: (anchor: FlowSlotAssetsWideAnchor) => void;
  listViewMode?: Mode;
  onListViewModeChange?: (mode: Mode) => void;
};

/** Shown when the flow has no ORGANIZE copy yet (not published). */
export function FlowSlotAssetsUnpublishedPlaceholder() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 3,
        py: 3,
        textAlign: 'center',
      }}
    >
      <PublishOutlined sx={{ fontSize: 40, color: 'info.main', mb: 0.5 }} />
      <Typography variant="subtitle1" fontWeight={600} color="text.primary">
        {t('flows.detail.slotAssets.publishToManageTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
        {t('flows.detail.slotAssets.publishToManageHint')}
      </Typography>
    </Box>
  );
}

export function resolveDefaultSlotAssetsListMode(hasPlanMode: boolean, hasOrganizeMode: boolean): Mode {
  if (hasPlanMode) return 'PLAN';
  if (hasOrganizeMode) return 'ORGANIZE';
  return 'PLAN';
}

const slotAssetsModeButtonSx = {
  flex: 1,
  minWidth: 88,
};

/** Draft / published toggle + read-only slot list (wide + narrow slot assets layouts). */
export function FlowSlotAssetsSlotListColumn({
  setShowFlowDetails,
  setFlowDetailsDirection,
  onSlotSelect,
  onShowTimingAlert,
  onShowPublishAlert,
  middleSectionOpensOverlayOnSmallScreen = false,
  flowSlotAssetsWideAnchor,
  onFlowSlotAssetsWideAnchorChange,
  listViewMode: listViewModeProp,
  onListViewModeChange: onListViewModeChangeProp,
}: FlowSlotAssetsSlotListColumnProps) {
  const { t } = useTranslation();
  const { flowId } = useParams();
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const hasPlanMode = !!flowPlan;
  const hasOrganizeMode = !!flowOrganize;
  const [internalListViewMode, setInternalListViewMode] = useState<Mode>(() =>
    resolveDefaultSlotAssetsListMode(hasPlanMode, hasOrganizeMode),
  );
  const listViewMode = listViewModeProp ?? internalListViewMode;
  const onListViewModeChange = onListViewModeChangeProp ?? setInternalListViewMode;

  useEffect(() => {
    if (listViewModeProp !== undefined) return;
    setInternalListViewMode((current) => {
      if (current === 'PLAN' && !hasPlanMode && hasOrganizeMode) return 'ORGANIZE';
      if (current === 'ORGANIZE' && !hasOrganizeMode && hasPlanMode) return 'PLAN';
      return current;
    });
  }, [hasPlanMode, hasOrganizeMode, listViewModeProp]);

  const { label: planModeButtonLabel, color: planModeButtonColor } = useFlowPlanModeHeaderState(flowId);
  const effectiveFlowPlanForPublish = useMemo(
    () => flowPlan ?? (flow?.mode === 'PLAN' ? flow : null),
    [flowPlan, flow],
  );
  if (!hasPlanMode && !hasOrganizeMode) {
    return (
      <Box sx={slotListColumnOuterSx}>
        <FlowSlotAssetsUnpublishedPlaceholder />
      </Box>
    );
  }

  return (
    <Box sx={slotListColumnOuterSx}>
      <Stack direction="row" alignItems="center" spacing={0} sx={{ ...slotListColumnHeaderInsetSx, width: '100%' }}>
        {hasPlanMode ? (
          <Button
            variant={listViewMode === 'PLAN' ? 'contained' : 'outlined'}
            color={planModeButtonColor}
            size="small"
            onClick={() => onListViewModeChange('PLAN')}
            disabled={!hasPlanMode}
            startIcon={<ListOutlined fontSize="small" />}
            sx={slotAssetsModeButtonSx}
          >
            {planModeButtonLabel}
          </Button>
        ) : null}
        {hasPlanMode ? (
          <Stack direction="row" alignItems="center" spacing={0} sx={{ flexShrink: 0 }}>
            <ArrowRightOutlined fontSize="small" color="action" />
            {flow ? (
              <Publish
                flow={flow}
                buttonOrIcon="icon"
                context="flowDetail"
                flowPlanOverride={effectiveFlowPlanForPublish ?? undefined}
                onIndicativeClick={onShowPublishAlert}
              />
            ) : null}
            <ArrowRightOutlined fontSize="small" color="action" />
          </Stack>
        ) : null}
        <Button
          variant={listViewMode === 'ORGANIZE' ? 'contained' : 'outlined'}
          color="secondary"
          size="small"
          onClick={() => onListViewModeChange('ORGANIZE')}
          disabled={!hasOrganizeMode}
          startIcon={<VisibilityOutlined fontSize="small" />}
          sx={slotAssetsModeButtonSx}
        >
          {t('flows.detail.slotAssets.listModeContributor')}
        </Button>
      </Stack>
      <Box sx={slotListScrollAreaSx}>
        <SlotList
          setShowFlowDetails={setShowFlowDetails}
          setFlowDetailsDirection={setFlowDetailsDirection}
          onSlotSelect={onSlotSelect}
          onShowTimingAlert={onShowTimingAlert}
          onShowPublishAlert={onShowPublishAlert}
          modeOverride={listViewMode}
          readOnly
          startRowMediaOpenDetailsMode="flowSlotAssets"
          middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
          smallScreenInlineDetailsVariant="slotAssets"
          flowSlotAssetsWideAnchor={flowSlotAssetsWideAnchor}
          onFlowSlotAssetsWideAnchorChange={onFlowSlotAssetsWideAnchorChange}
          hideEmptyUntimedSection
        />
      </Box>
    </Box>
  );
}
